import { useState } from 'react';

import {
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    Typography,
    useMediaQuery,
} from '@mui/material';
import { format } from 'date-fns';

interface User {
    id: number;
    firstName: string;
    lastName: string;
}

interface HistoryLogEntry {
    id: number;
    details: string;
    createdAt: string;
    user: User;
}

interface HistoryLogProps {
    historyLog: HistoryLogEntry[];
}

const HistoryTable = ({ historyLog }: HistoryLogProps) => {
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(5);
    const isSmallScreen = useMediaQuery('(max-width:768px)');

    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const paginatedHistoryLog = historyLog.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
    );

    return (
        <TableContainer
            component={Paper}
            sx={{
                border: '1px solid',
                borderColor: 'border.primary',
                boxShadow: 'shadow.primary',
            }}
        >
            <Typography
                fontWeight="500"
                fontSize={{ xs: '18px', sm: '24px' }}
                lineHeight="34px"
                color="text.primary"
                sx={{ margin: '10px' }}
            >
                History
            </Typography>
            <Table sx={{ minWidth: 650 }} aria-label="history table">
                <TableHead>
                    <TableRow
                        sx={{
                            bgcolor: 'grey.500',
                            borderBottom: '2px solid',
                            borderBottomColor: 'border.primary',
                        }}
                    >
                        {!isSmallScreen && (
                            <>
                                <TableCell style={{ width: '20%' }}>
                                    <Typography color="text.primary" fontWeight="bold">
                                        Date/Time
                                    </Typography>
                                </TableCell>
                                <TableCell style={{ width: '60%' }}>
                                    <Typography color="text.primary" fontWeight="bold">
                                        Description
                                    </Typography>
                                </TableCell>
                                <TableCell style={{ width: '20%' }}>
                                    <Typography color="text.primary" fontWeight="bold">
                                        Added By
                                    </Typography>
                                </TableCell>
                            </>
                        )}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {paginatedHistoryLog.map((row, index) => {
                        const logDate = format(new Date(row.createdAt), 'yyyy-MM-dd HH:mm');
                        const fullName = `${row.user?.firstName} ${row.user?.lastName}`;

                        return (
                            <TableRow
                                key={row.id}
                                sx={{ bgcolor: index % 2 !== 0 ? '#FFFAE5' : 'white' }}
                            >
                                {!isSmallScreen ? (
                                    <>
                                        <TableCell>{logDate}</TableCell>
                                        <TableCell>{row.details}</TableCell>
                                        <TableCell>{fullName}</TableCell>
                                    </>
                                ) : (
                                    <TableCell>
                                        <Typography>
                                            <strong>Date/Time:</strong> {logDate}
                                        </Typography>
                                        <Typography>
                                            <strong>Description:</strong> {row.details}
                                        </Typography>
                                        <Typography>
                                            <strong>Added By:</strong> {fullName}
                                        </Typography>
                                    </TableCell>
                                )}
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>
            <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component="div"
                count={historyLog.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
            />
        </TableContainer>
    );
};

export default HistoryTable;
